/* TWM Lottie animations and the offline screen.

   [data-twm-lottie] elements (snippets/twm-lottie.liquid) play the Lottie
   JSON they point at with lottie-web (assets/twm-lottie-player.min.js,
   loaded only when needed). Visitors who prefer reduced motion get one
   still frame. If the player or JSON can't load, the snippet's fallback
   icon stays.

   The offline screen (snippets/twm-offline.liquid) opens on the browser's
   "offline" event. Once the page has loaded, the player and the animation
   are fetched at idle priority so they are in the cache when the network
   drops. */
(() => {
  const script = document.currentScript
  const PLAYER = script && script.dataset.player
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')
  const STILL_FRAME = 40
  let player = null

  const loadPlayer = () => {
    if (window.lottie) return Promise.resolve(window.lottie)
    if (player) return player
    player = new Promise((resolve, reject) => {
      const tag = document.createElement('script')
      tag.src = PLAYER
      tag.onload = () => (window.lottie ? resolve(window.lottie) : reject(new Error('lottie missing')))
      tag.onerror = reject
      document.head.appendChild(tag)
    }).catch((error) => {
      player = null
      throw error
    })
    return player
  }

  const play = (el) => {
    if (!el) return
    // Already loaded: a play-once animation starts again from the top.
    if (el.twmLottie && typeof el.twmLottie === 'object') {
      if (el.hasAttribute('data-twm-lottie-once')) el.twmLottie.goToAndPlay(0, true)
      return
    }
    if (el.twmLottie) return
    el.twmLottie = 'loading'
    const once = el.hasAttribute('data-twm-lottie-once')
    loadPlayer()
      .then((lottie) => {
        const anim = lottie.loadAnimation({
          container: el,
          renderer: 'svg',
          loop: !once,
          autoplay: !reduce.matches,
          path: el.dataset.twmLottie,
          // A play-once burst fills its box; illustrations fit inside theirs.
          rendererSettings: { preserveAspectRatio: once ? 'xMidYMid slice' : 'xMidYMid meet' },
        })
        anim.addEventListener('DOMLoaded', () => {
          if (reduce.matches) anim.goToAndStop(STILL_FRAME, true)
          el.classList.add('is-ready')
          el.twmLottie = anim
        })
        anim.addEventListener('data_failed', () => {
          anim.destroy()
          el.twmLottie = null
        })
      })
      .catch(() => {
        el.twmLottie = null
      })
  }

  document.querySelectorAll('[data-twm-lottie]:not([data-twm-lottie-defer])').forEach(play)
  window.twmLottie = { play }

  /* ---------- Cart drawer: confetti once the cart ships free ----------
     Plays when the open drawer shows the "qualifies for free shipping"
     message, once per visit; dropping below the threshold re-arms it.
     The drawer re-renders its contents on every change, so the check
     runs on any change inside it. */
  const drawer = document.querySelector('cart-drawer')
  if (drawer && !reduce.matches) {
    const KEY = 'twm-free-shipping-celebrated'
    const store = {
      get: () => { try { return sessionStorage.getItem(KEY) } catch (e) { return '1' } },
      set: (v) => { try { v ? sessionStorage.setItem(KEY, '1') : sessionStorage.removeItem(KEY) } catch (e) {} },
    }
    let queued = false
    const check = () => {
      queued = false
      if (!drawer.classList.contains('active')) return
      const success = drawer.querySelector('.cart-shipping__message_success')
      if (!success) return
      if (!success.classList.contains('active')) return store.set(false)
      if (store.get()) return
      store.set(true)
      const confetti = drawer.querySelector('.twm-cart-confetti')
      if (!confetti) return
      confetti.classList.add('is-playing')
      play(confetti)
      clearTimeout(confetti.twmHide)
      confetti.twmHide = setTimeout(() => confetti.classList.remove('is-playing'), 5200)
    }
    new MutationObserver(() => {
      if (queued) return
      queued = true
      setTimeout(check, 120)
    }).observe(drawer, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true })
  }

  /* ---------- Offline screen ---------- */
  const screen = document.querySelector('[data-twm-offline]')
  if (!screen) return
  const art = screen.querySelector('[data-twm-lottie]')
  const retry = screen.querySelector('[data-twm-offline-retry]')
  let lastFocus = null
  let dismissed = false

  const open = () => {
    if (dismissed || !screen.hidden) return
    lastFocus = document.activeElement
    screen.hidden = false
    document.documentElement.classList.add('twm-is-offline')
    screen.classList.remove('is-back')
    retry.textContent = 'Try again'
    play(art)
    retry.focus({ preventScroll: true })
  }

  const close = () => {
    if (screen.hidden) return
    screen.hidden = true
    document.documentElement.classList.remove('twm-is-offline')
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true })
  }

  window.addEventListener('offline', () => {
    dismissed = false
    open()
  })
  window.addEventListener('online', () => {
    if (screen.hidden) return
    screen.classList.add('is-back')
    retry.textContent = 'Back online. Reload page'
  })

  retry.addEventListener('click', () => {
    if (navigator.onLine) window.location.reload()
    else {
      screen.classList.remove('is-shake')
      void screen.offsetWidth
      screen.classList.add('is-shake')
    }
  })
  screen.querySelector('[data-twm-offline-close]').addEventListener('click', () => {
    dismissed = true
    close()
  })
  screen.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      dismissed = true
      close()
    }
  })

  if (navigator.onLine === false) open()

  // Warm the cache for the offline screen once the page is idle.
  const warm = () => {
    const conn = navigator.connection
    if (conn && conn.saveData) return
    ;[PLAYER, art && art.dataset.twmLottie].forEach((url) => {
      if (url) fetch(url, { priority: 'low' }).catch(() => {})
    })
  }
  window.addEventListener('load', () => {
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 8000 })
    else setTimeout(warm, 4000)
  })
})()
